import React, { useState } from 'react';
import { Chip, Menu, MenuItem, CircularProgress, Box } from '@mui/material';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { AutomationStatusEnum, ExecutionModeEnum } from '../../enums/enumRegistry';
import { useSnackbar } from '../../utils/useSnackbar';

const AutomationStatusChip = ({ 
  value, 
  onUpdate, 
  disabled = false, 
  testCaseId,
  isEditing = false 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { showSnackbar } = useSnackbar();    
  const handleClick = (event) => {
    if (!disabled && !isUpdating) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === value) {
      handleClose();
      return;
    }

    // Close dropdown immediately
    handleClose();
    
    setIsUpdating(true);

    try {
      // Determine execution mode based on automation status
      let executionMode = null;
      if (newStatus === AutomationStatusEnum.NOT_AUTOMATED) {
        executionMode = ExecutionModeEnum.MANUAL;
      } else if (newStatus === AutomationStatusEnum.NLP_AUTOMATED) {
        executionMode = ExecutionModeEnum.NLP;
      } else if (newStatus === AutomationStatusEnum.EXTERNALLY_AUTOMATED) {
        executionMode = ExecutionModeEnum.EXTERNAL;
      } else if (newStatus === AutomationStatusEnum.IN_PROGRESS) {
        executionMode = null;
      }

      await onUpdate({
        automation_status: newStatus,
        execution_mode: executionMode
      });

    //   showSnackbar('Automation status updated successfully!', 'success');
    } catch (err) {
    //   showSnackbar( 'Failed to update automation status', 'error');
      console.error('Error in AutomationStatusChip:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const getChipColor = (status) => {
    switch (status) {
      case AutomationStatusEnum.NOT_AUTOMATED:
        return { bg: '#f3f4f6', color: '#6b7280' };
      case AutomationStatusEnum.IN_PROGRESS:
        return { bg: '#fef3c7', color: '#d97706' };
      case AutomationStatusEnum.NLP_AUTOMATED:
        return { bg: '#f3e8ff', color: '#a21caf' };
      case AutomationStatusEnum.EXTERNALLY_AUTOMATED:
        return { bg: '#dcfce7', color: '#16a34a' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case AutomationStatusEnum.NOT_AUTOMATED:
        return <ExclamationTriangleIcon style={{ width: 12, height: 12 }} />;
      case AutomationStatusEnum.IN_PROGRESS:
        return <CircularProgress size={12} thickness={4} />;
      case AutomationStatusEnum.NLP_AUTOMATED:
        return <CheckCircleIcon style={{ width: 12, height: 12 }} />;
      case AutomationStatusEnum.EXTERNALLY_AUTOMATED:
        return <CheckCircleIcon style={{ width: 12, height: 12 }} />;
      default:
        return null;
    }
  };

  const colors = getChipColor(value);
//   const icon = getStatusIcon(value);

  return (
    <Box>
      <Chip
        label={value}
        onClick={handleClick}
        disabled={disabled || isUpdating}
        // icon={isUpdating ? <CircularProgress size={12} thickness={4} /> : icon}
        sx={{
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 500,
          fontSize: 12,
          height: 24,
          lineHeight: '20px',
          cursor: disabled || isUpdating ? 'default' : 'pointer',
          '&:hover': {
            backgroundColor: disabled || isUpdating ? colors.bg : `${colors.bg}dd`,
          },
          '& .MuiChip-label': {
            px: 1.5,
          },
        }}
      />
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          }
        }}
      >
        {Object.values(AutomationStatusEnum).map((status) => {
          const statusColors = getChipColor(status);
          const statusIcon = getStatusIcon(status);
          
          return (
            <MenuItem
              key={status}
              onClick={() => handleStatusChange(status)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: '#f8fafc',
                }
              }}
            >
              {/* <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  backgroundColor: statusColors.bg,
                  color: statusColors.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1
                }}
              >
                {statusIcon}
              </Box> */}
              <span style={{ fontSize: 12, fontWeight: 500 }}>{status}</span>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default AutomationStatusChip;
